if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        /* Deferred slides (perf round 2, C1): galleries inserted after the
           hydration script ran (quick add, product swap) register here. */
        window.productMediaHydrate?.register(this);

        /* D-19.5: the Staffelrabatte tier selector announces the selected
           tier's pack-shot (quantity-tiers.js). Live's gallery listens for
           the same announcement (media-gallery.js onBulkPriceImageChange):
           activate the gallery media whose image matches, else swap the
           visible image's sources (originals kept on data-*). Registered
           before the thumbnails guard so thumbnail-less layouts react too. */
        document.addEventListener('quantity-tiers:image-change', this.onTierImageChange.bind(this));

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
          /* A hidden variant slide revealed by an earlier activation goes
             back into hiding when another slide takes over — live's
             variant-image management (media-gallery.js:117-131 in the
             snapshot). */
          if (element !== activeMedia && element.dataset.variantSlideRevealed) {
            delete element.dataset.variantSlideRevealed;
            element.classList.add('product__media-item--variant-hidden');
            element.style.display = 'none';
          }
        });
        activeMedia?.classList?.add('is-active');

        /* Live's setActiveMedia unhides the media it activates
           (media-gallery.js:134-140 in the snapshot). The localized augment
           modes render each variant's featured media as a display:none
           slide (class + inline style); a client-side variant switch with
           no section re-render — the bundle set picker — activates one of
           those directly, and without this the gallery has an is-active
           slide that stays hidden, so the thumbnail/thumbnail_slider
           desktop layouts show nothing at all. Scoped to the hidden-variant
           slides so the default gallery keeps its class-driven behaviour
           (product-gallery-nav.js copies server state on re-renders). */
        if (activeMedia.classList.contains('product__media-item--variant-hidden')) {
          activeMedia.classList.remove('product__media-item--variant-hidden');
          activeMedia.classList.remove('product__media-item--variant-mobile-hide');
          activeMedia.style.display = '';
          activeMedia.dataset.variantSlideRevealed = 'true';
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            /* Live never prepends variant thumbnails — they keep their slot
               right after the featured thumb (media-gallery.js:146-152). */
            if (activeThumbnail && !activeThumbnail.hasAttribute('data-variant-image')) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        /* A deferred slide gets its sources (and its neighbours theirs) once
           it is visible and in its final position (assets/product-media-hydrate.js). */
        window.productMediaHydrate?.hydrateAround(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        /* Live's variant-thumb visibility (media-gallery.js:200-219 in the
           snapshot): the hidden variant thumbnails reveal only while their
           variant's media is the active one; the previously revealed one
           hides again. Non-variant thumbs leave them untouched, exactly as
           live does — the selected variant's thumb stays reachable. */
        if (thumbnail.hasAttribute('data-variant-image')) {
          this.elements.thumbnails.querySelectorAll('li[data-variant-image="true"]').forEach((element) => {
            if (element !== thumbnail) element.style.display = 'none';
          });
          thumbnail.style.display = '';
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      /* Lean port of live's onBulkPriceImageChange: match by size-stripped
         filename against the gallery media; a match is activated (its own
         full-res sources are correct), otherwise the active/first media's
         image is swapped to the announced sources with the originals stored
         once on data attributes — exactly live's fallback. */
      onTierImageChange(event) {
        const { imageSrc, imageSrcset, imageAlt } = event.detail || {};
        if (!imageSrc || !this.elements.viewer) return;

        const stripSize = (url) => url.split('?')[0].replace(/_\d+x\d*\./, '.');
        const filename = (url) => stripSize(url).split('/').pop().split('.')[0];
        const targetFilename = filename(imageSrc);

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        if (!mediaItems.length) return;

        /* A deferred slide carries its source in data-src until hydrated. */
        const sourceOf = (img) => img.getAttribute('src') || img.getAttribute('data-src') || '';
        const match = mediaItems.find((media) => {
          const img = media.querySelector('img');
          return img && sourceOf(img) && filename(sourceOf(img)) === targetFilename;
        });
        if (match) {
          this.setActiveMedia(match.dataset.mediaId, false);
          return;
        }

        const target = mediaItems.find((media) => media.classList.contains('is-active')) || mediaItems[0];
        /* Hydrate first so the originals below are the real sources and the
           swap is never overwritten by a later hydration. */
        window.productMediaHydrate?.hydrateSlide(target);
        const targetImage = target.querySelector('img');
        if (!targetImage) return;
        if (!targetImage.dataset.originalSrc) {
          targetImage.dataset.originalSrc = targetImage.src;
          targetImage.dataset.originalSrcset = targetImage.srcset;
          targetImage.dataset.originalAlt = targetImage.alt;
        }
        targetImage.src = imageSrc;
        if (imageSrcset) targetImage.srcset = imageSrcset;
        if (imageAlt) targetImage.alt = imageAlt;
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
